export interface PackChoice {
  id: string;
  name: string;
  description: string;
  artUrl: string;
  label: string;
  primary: string;
  secondary: string;
}

interface PackCarouselState {
  choices: PackChoice[];
  selectedId: string;
  loading: boolean;
  remaining: number;
}

const styles = `
  :host {
    display: block;
    min-width: 0;
  }

  * {
    box-sizing: border-box;
  }

  button {
    font: inherit;
    -webkit-tap-highlight-color: transparent;
  }

  .carousel {
    position: relative;
    min-width: 0;
    padding: 4px 0 0;
  }

  .viewport {
    display: flex;
    gap: 18px;
    overflow-x: auto;
    overscroll-behavior-inline: contain;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    padding: 8px calc((100% - min(68vw, 258px)) / 2) 22px;
    touch-action: pan-x;
  }

  .viewport::-webkit-scrollbar {
    display: none;
  }

  .pack-choice {
    --pack-primary: #80dfff;
    --pack-secondary: #e16f9d;
    --rx: 0deg;
    --ry: 0deg;
    --mx: 50%;
    --my: 30%;
    flex: 0 0 min(68vw, 258px);
    scroll-snap-align: center;
    border: 0;
    padding: 0;
    color: #f7efda;
    background: transparent;
    text-align: left;
    perspective: 1000px;
    opacity: 0.46;
    transform: scale(0.86);
    transition: opacity 240ms ease, transform 320ms cubic-bezier(0.2, 0.9, 0.2, 1);
  }

  .pack-choice[data-selected='true'] {
    opacity: 1;
    transform: scale(1);
  }

  .foil-pack {
    position: relative;
    display: block;
    width: 100%;
    aspect-ratio: 0.68;
    overflow: hidden;
    border-radius: 15px 15px 20px 20px;
    border: 1px solid rgba(255,255,255,0.38);
    background:
      linear-gradient(135deg, rgba(255,255,255,0.68), transparent 14% 36%, rgba(255,255,255,0.26) 48%, transparent 62%),
      linear-gradient(160deg, var(--pack-primary), #12151d 48%, var(--pack-secondary));
    box-shadow:
      0 30px 48px rgba(0,0,0,0.5),
      0 4px 0 rgba(255,255,255,0.14) inset,
      0 0 0 1px rgba(0,0,0,0.38) inset;
    transform: rotateX(var(--rx)) rotateY(var(--ry));
    transform-style: preserve-3d;
    transition: transform 160ms ease, box-shadow 240ms ease;
  }

  .pack-choice[data-selected='true'] .foil-pack {
    box-shadow:
      0 34px 58px rgba(0,0,0,0.58),
      0 0 32px color-mix(in oklab, var(--pack-primary) 24%, transparent),
      0 4px 0 rgba(255,255,255,0.18) inset,
      0 0 0 1px rgba(0,0,0,0.38) inset;
  }

  .crimp {
    position: absolute;
    z-index: 6;
    left: 0;
    right: 0;
    height: 21px;
    background:
      linear-gradient(180deg, rgba(255,255,255,0.58), transparent 34%, rgba(0,0,0,0.28) 76%, rgba(255,255,255,0.18)),
      repeating-linear-gradient(90deg, rgba(255,255,255,0.36) 0 2px, rgba(0,0,0,0.2) 2px 5px),
      var(--pack-primary);
    box-shadow: 0 1px rgba(255,255,255,0.36), 0 5px 10px rgba(0,0,0,0.22);
  }

  .crimp.top {
    top: 0;
  }

  .crimp.bottom {
    bottom: 0;
    transform: rotate(180deg);
  }

  .art {
    position: absolute;
    inset: 20px 0;
    background-position: center;
    background-size: cover;
    filter: saturate(1.04) contrast(1.05);
  }

  .art::before,
  .art::after,
  .foil-pack::before,
  .foil-pack::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
  }

  .art::before {
    background:
      linear-gradient(180deg, rgba(6,8,14,0.12) 14%, transparent 42%, rgba(6,8,14,0.88) 88%),
      linear-gradient(135deg, color-mix(in oklab, var(--pack-primary) 46%, transparent), transparent 38% 62%, color-mix(in oklab, var(--pack-secondary) 52%, transparent));
  }

  .art::after {
    background: radial-gradient(circle at 50% 34%, transparent 0 24%, rgba(4,6,12,0.24) 68%, rgba(4,6,12,0.68));
  }

  .foil-pack::before {
    z-index: 7;
    left: -60%;
    width: 80%;
    background: linear-gradient(105deg, transparent 20%, rgba(255,255,255,0.56) 48%, transparent 74%);
    mix-blend-mode: screen;
    transform: translateX(var(--mx));
    opacity: 0.58;
  }

  .foil-pack::after {
    z-index: 5;
    background:
      repeating-linear-gradient(112deg, rgba(255,255,255,0.08) 0 1px, transparent 1px 7px),
      radial-gradient(circle at var(--mx) var(--my), rgba(255,255,255,0.28), transparent 34%);
    mix-blend-mode: screen;
  }

  .pack-copy {
    position: absolute;
    z-index: 8;
    left: 14px;
    right: 14px;
    bottom: 35px;
    display: grid;
    gap: 6px;
    padding: 14px;
    border: 1px solid rgba(255,255,255,0.3);
    border-radius: 11px;
    background:
      linear-gradient(135deg, color-mix(in oklab, var(--pack-primary) 74%, #11151e), color-mix(in oklab, var(--pack-secondary) 62%, #0a0c12));
    box-shadow: 0 12px 28px rgba(0,0,0,0.34), inset 0 1px rgba(255,255,255,0.24);
    transform: translateZ(18px);
  }

  .pack-copy small,
  .pack-copy strong,
  .pack-copy span {
    display: block;
  }

  .pack-copy small {
    color: rgba(255,255,255,0.78);
    font: 900 0.61rem/1 var(--font-display, "Sora", sans-serif);
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  .pack-copy strong {
    color: #fffdf5;
    font: 900 1.35rem/0.94 var(--font-display, "Sora", sans-serif);
    text-transform: uppercase;
    text-wrap: balance;
  }

  .pack-copy span {
    color: rgba(255,255,255,0.76);
    font-size: 0.7rem;
  }

  .pack-label {
    display: grid;
    gap: 5px;
    padding: 13px 4px 0;
    text-align: center;
  }

  .pack-label strong {
    color: #fff8e8;
    font: 900 0.98rem/1.1 var(--font-display, "Sora", sans-serif);
  }

  .pack-label span {
    min-height: 2.5em;
    color: rgba(247,239,218,0.6);
    font-size: 0.76rem;
    line-height: 1.28;
  }

  .arrow {
    position: absolute;
    z-index: 12;
    top: 42%;
    width: 38px;
    aspect-ratio: 1;
    display: grid;
    place-items: center;
    border: 1px solid rgba(247,239,218,0.18);
    border-radius: 50%;
    color: #f7efda;
    background: rgba(8,10,16,0.78);
    box-shadow: 0 10px 22px rgba(0,0,0,0.32);
    backdrop-filter: blur(12px);
  }

  .arrow:first-of-type {
    left: 2px;
  }

  .arrow:last-of-type {
    right: 2px;
  }

  .arrow:disabled {
    opacity: 0.28;
  }

  .controls {
    display: grid;
    gap: 10px;
    justify-items: center;
  }

  .dots {
    display: flex;
    gap: 7px;
  }

  .dot {
    width: 6px;
    height: 6px;
    border: 0;
    border-radius: 999px;
    padding: 0;
    background: rgba(247,239,218,0.28);
    transition: width 200ms ease, background 200ms ease;
  }

  .dot[data-selected='true'] {
    width: 24px;
    background: var(--dot-color, #80dfff);
  }

  .open {
    width: min(100%, 340px);
    min-height: 52px;
    border: 0;
    border-radius: 12px;
    padding: 0 18px;
    color: #080b11;
    background: linear-gradient(135deg, var(--selected-primary, #80dfff), var(--selected-secondary, #f5c76b));
    box-shadow: 0 14px 30px color-mix(in oklab, var(--selected-primary, #80dfff) 22%, transparent);
    font: 900 0.88rem/1 var(--font-display, "Sora", sans-serif);
    text-transform: uppercase;
  }

  .open:not(:disabled):active {
    transform: translateY(1px) scale(0.99);
  }

  .open:disabled {
    opacity: 0.45;
  }

  .swipe-hint {
    color: rgba(247,239,218,0.52);
    font: 800 0.66rem/1 var(--font-display, "Sora", sans-serif);
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  @media (hover: none) {
    .arrow {
      display: none;
    }
  }

  @media (max-width: 374px) {
    .viewport {
      gap: 14px;
    }

    .pack-copy strong {
      font-size: 1.16rem;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .pack-choice,
    .foil-pack,
    .dot {
      transition: none;
    }
  }
`;

export class PackCarouselElement extends HTMLElement {
  private readonly root: ShadowRoot;
  private choices: PackChoice[] = [];
  private selectedId = '';
  private loading = false;
  private remaining = 0;
  private scrollTimer: number | null = null;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    this.render();
  }

  disconnectedCallback(): void {
    if (this.scrollTimer !== null) {
      window.clearTimeout(this.scrollTimer);
    }
  }

  setState(state: PackCarouselState): void {
    this.choices = [...state.choices];
    this.selectedId = state.selectedId;
    this.loading = state.loading;
    this.remaining = state.remaining;
    this.render();
  }

  private render(): void {
    const selectedIndex = Math.max(0, this.choices.findIndex((choice) => choice.id === this.selectedId));
    const selected = this.choices[selectedIndex];
    const locked = this.remaining <= 0;

    this.root.innerHTML = `
      <style>${styles}</style>
      <div class="carousel">
        <button class="arrow previous" type="button" aria-label="Previous pack" ${selectedIndex <= 0 ? 'disabled' : ''}>←</button>
        <div class="viewport" aria-label="Choose a mate pack">
          ${this.choices.map((choice) => this.renderChoice(choice)).join('')}
        </div>
        <button class="arrow next" type="button" aria-label="Next pack" ${selectedIndex >= this.choices.length - 1 ? 'disabled' : ''}>→</button>
      </div>
      <div class="controls" style="--selected-primary:${selected?.primary ?? '#80dfff'};--selected-secondary:${selected?.secondary ?? '#f5c76b'}">
        <div class="dots" aria-hidden="true">
          ${this.choices
            .map(
              (choice) =>
                `<button class="dot" type="button" data-pack-dot="${escapeAttribute(choice.id)}" data-selected="${choice.id === this.selectedId}" style="--dot-color:${choice.primary}" tabindex="-1"></button>`
            )
            .join('')}
        </div>
        <button class="open" type="button" ${this.loading || locked ? 'disabled' : ''}>
          ${this.loading ? 'Sealing your booster…' : locked ? 'Vault empty' : `Choose ${escapeHtml(selected?.label ?? 'pack')}`}
        </button>
        <span class="swipe-hint">Swipe to choose · then rip it open</span>
      </div>
    `;

    const viewport = this.root.querySelector<HTMLElement>('.viewport');
    const selectedCard = this.root.querySelector<HTMLElement>(`[data-pack-id="${escapeSelector(this.selectedId)}"]`);

    window.requestAnimationFrame(() => {
      if (viewport && selectedCard) {
        viewport.scrollLeft = selectedCard.offsetLeft - (viewport.clientWidth - selectedCard.clientWidth) / 2;
      }
    });

    viewport?.addEventListener('scroll', () => this.handleScroll(viewport), { passive: true });
    this.root.querySelector('.previous')?.addEventListener('click', () => this.selectByOffset(-1));
    this.root.querySelector('.next')?.addEventListener('click', () => this.selectByOffset(1));
    this.root.querySelector('.open')?.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('press', { bubbles: true, composed: true }));
    });

    this.root.querySelectorAll<HTMLElement>('.pack-choice').forEach((card) => {
      card.addEventListener('click', () => {
        const id = card.dataset.packId;
        if (id) {
          this.select(id, true);
        }
      });
      card.addEventListener('pointermove', (event) => this.updateTilt(card, event));
      card.addEventListener('pointerleave', () => this.resetTilt(card));
      card.addEventListener('pointerup', () => this.resetTilt(card));
    });

    this.root.querySelectorAll<HTMLElement>('[data-pack-dot]').forEach((dot) => {
      dot.addEventListener('click', () => {
        const id = dot.dataset.packDot;
        if (id) {
          this.select(id, true);
        }
      });
    });
  }

  private renderChoice(choice: PackChoice): string {
    return `
      <button
        class="pack-choice"
        type="button"
        data-pack-id="${escapeAttribute(choice.id)}"
        data-selected="${choice.id === this.selectedId}"
        style="--pack-primary:${choice.primary};--pack-secondary:${choice.secondary}"
        aria-label="Select ${escapeAttribute(choice.name)}"
      >
        <span class="foil-pack">
          <span class="crimp top"></span>
          <span class="art" style="background-image:url('${escapeStyleUrl(choice.artUrl)}')"></span>
          <span class="pack-copy">
            <small>Mates Gacha · 5 cards</small>
            <strong>${escapeHtml(choice.name)}</strong>
            <span>${escapeHtml(choice.label)}</span>
          </span>
          <span class="crimp bottom"></span>
        </span>
        <span class="pack-label">
          <strong>${escapeHtml(choice.name)}</strong>
          <span>${escapeHtml(choice.description)}</span>
        </span>
      </button>
    `;
  }

  private handleScroll(viewport: HTMLElement): void {
    if (this.scrollTimer !== null) {
      window.clearTimeout(this.scrollTimer);
    }
    this.scrollTimer = window.setTimeout(() => {
      const center = viewport.scrollLeft + viewport.clientWidth / 2;
      const cards = [...this.root.querySelectorAll<HTMLElement>('.pack-choice')];
      const closest = cards.reduce<HTMLElement | null>((best, card) => {
        if (!best) {
          return card;
        }
        const cardCenter = card.offsetLeft + card.clientWidth / 2;
        const bestCenter = best.offsetLeft + best.clientWidth / 2;
        return Math.abs(cardCenter - center) < Math.abs(bestCenter - center) ? card : best;
      }, null);
      const id = closest?.dataset.packId;
      if (id && id !== this.selectedId) {
        this.select(id, false);
      }
    }, 90);
  }

  private selectByOffset(offset: number): void {
    const currentIndex = Math.max(0, this.choices.findIndex((choice) => choice.id === this.selectedId));
    const next = this.choices[Math.max(0, Math.min(this.choices.length - 1, currentIndex + offset))];
    if (next) {
      this.select(next.id, true);
    }
  }

  private select(id: string, scroll: boolean): void {
    if (id === this.selectedId && !scroll) {
      return;
    }
    this.selectedId = id;
    this.updateSelectedState();
    if (scroll) {
      this.scrollToChoice(id);
    }
    this.dispatchEvent(
      new CustomEvent<{ id: string }>('change', {
        detail: { id },
        bubbles: true,
        composed: true
      })
    );
  }

  private updateSelectedState(): void {
    const selected = this.choices.find((choice) => choice.id === this.selectedId);
    this.root.querySelectorAll<HTMLElement>('.pack-choice').forEach((card) => {
      card.dataset.selected = String(card.dataset.packId === this.selectedId);
    });
    this.root.querySelectorAll<HTMLElement>('[data-pack-dot]').forEach((dot) => {
      dot.dataset.selected = String(dot.dataset.packDot === this.selectedId);
    });
    const controls = this.root.querySelector<HTMLElement>('.controls');
    const open = this.root.querySelector<HTMLButtonElement>('.open');
    if (controls && selected) {
      controls.style.setProperty('--selected-primary', selected.primary);
      controls.style.setProperty('--selected-secondary', selected.secondary);
    }
    if (open && selected && !this.loading && this.remaining > 0) {
      open.textContent = `Choose ${selected.label}`;
    }
    const index = this.choices.findIndex((choice) => choice.id === this.selectedId);
    const previous = this.root.querySelector<HTMLButtonElement>('.previous');
    const next = this.root.querySelector<HTMLButtonElement>('.next');
    if (previous) {
      previous.disabled = index <= 0;
    }
    if (next) {
      next.disabled = index >= this.choices.length - 1;
    }
  }

  private scrollToChoice(id: string): void {
    const viewport = this.root.querySelector<HTMLElement>('.viewport');
    const card = this.root.querySelector<HTMLElement>(`[data-pack-id="${escapeSelector(id)}"]`);
    if (!viewport || !card) {
      return;
    }
    viewport.scrollTo({
      left: card.offsetLeft - (viewport.clientWidth - card.clientWidth) / 2,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  }

  private updateTilt(card: HTMLElement, event: PointerEvent): void {
    if (card.dataset.selected !== 'true' || event.pointerType === 'touch') {
      return;
    }
    const rect = card.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    card.style.setProperty('--ry', `${(x - 0.5) * 10}deg`);
    card.style.setProperty('--rx', `${(0.5 - y) * 8}deg`);
    card.style.setProperty('--mx', `${x * 100}%`);
    card.style.setProperty('--my', `${y * 100}%`);
  }

  private resetTilt(card: HTMLElement): void {
    card.style.setProperty('--ry', '0deg');
    card.style.setProperty('--rx', '0deg');
    card.style.setProperty('--mx', '50%');
    card.style.setProperty('--my', '30%');
  }
}

if (!customElements.get('pack-carousel')) {
  customElements.define('pack-carousel', PackCarouselElement);
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
}

function escapeAttribute(value: string): string {
  return escapeHtml(value).replaceAll('`', '&#96;');
}

function escapeStyleUrl(value: string): string {
  return value.replaceAll('\\', '\\\\').replaceAll("'", "\\'");
}

function escapeSelector(value: string): string {
  return CSS.escape(value);
}
